import { Colors } from '@amelio/ui-library';

import type { ObjectivePeriodStatusType } from '@/constants/ObjectivePeriodStatus';
import type { TranslationKey } from '@/i18n-key';
import type { CycleStatusTone } from '@/pages/CycleEvaluation/Dashboard/cycleStatus';
import type { ColorType, PillsStatus, PillsVariant } from '@amelio/ui-library';

import { ObjectivePeriodStatus } from '@/constants/ObjectivePeriodStatus';

/** The four period statuses carry the same names as the cycle group ones, so they map onto the
 * same pill tones the rest of the application already uses. */
const STATUS_TONES: Readonly<Record<ObjectivePeriodStatusType, CycleStatusTone>> = {
    [ObjectivePeriodStatus.DRAFT]: 'draft',
    [ObjectivePeriodStatus.ACTIVE]: 'active',
    [ObjectivePeriodStatus.DEACTIVATED]: 'inactive',
    [ObjectivePeriodStatus.COMPLETED]: 'completed',
};

const STATUS_LABEL_KEYS: Readonly<Record<ObjectivePeriodStatusType, TranslationKey>> = {
    [ObjectivePeriodStatus.DRAFT]: 'objectivePeriods.status.draft',
    [ObjectivePeriodStatus.ACTIVE]: 'objectivePeriods.status.active',
    [ObjectivePeriodStatus.DEACTIVATED]: 'objectivePeriods.status.deactivated',
    [ObjectivePeriodStatus.COMPLETED]: 'objectivePeriods.status.completed',
};

// Server data is cast into `ObjectivePeriodStatusType` without runtime validation, so a status
// outside the members above can still reach these getters despite the type.
const UNKNOWN_STATUS_TONE: CycleStatusTone = 'inactive';
const UNKNOWN_STATUS_LABEL_KEY: TranslationKey = 'objectivePeriods.status.deactivated';

export interface ObjectivePeriodStatusConfirmation {
    title: TranslationKey;
    message: TranslationKey;
    confirmLabel: TranslationKey;
}

/** Leaving Active in either direction is worth a confirmation: deactivating withdraws the period
 * from new objectives, completing it freezes it for good. Activating never needs one. The row's
 * menu and the edit modal's status field both ask through this table. */
const STATUS_CONFIRMATIONS: Readonly<
    Partial<Record<ObjectivePeriodStatusType, ObjectivePeriodStatusConfirmation>>
> = {
    [ObjectivePeriodStatus.DEACTIVATED]: {
        title: 'objectivePeriods.deactivateTitle',
        message: 'objectivePeriods.deactivateMessage',
        confirmLabel: 'objectivePeriods.deactivate',
    },
    [ObjectivePeriodStatus.COMPLETED]: {
        title: 'objectivePeriods.completeTitle',
        message: 'objectivePeriods.completeMessage',
        confirmLabel: 'objectivePeriods.complete',
    },
};

/** The confirmation moving a period to `status` asks for, or undefined when it asks for none. */
export const getObjectivePeriodStatusConfirmation = (
    status: ObjectivePeriodStatusType,
): ObjectivePeriodStatusConfirmation | undefined =>
    Object.hasOwn(STATUS_CONFIRMATIONS, status) ? STATUS_CONFIRMATIONS[status] : undefined;

export const getObjectivePeriodStatusTone = (status: ObjectivePeriodStatusType): CycleStatusTone =>
    Object.hasOwn(STATUS_TONES, status) ? STATUS_TONES[status] : UNKNOWN_STATUS_TONE;

export const getObjectivePeriodStatusLabelKey = (
    status: ObjectivePeriodStatusType,
): TranslationKey =>
    Object.hasOwn(STATUS_LABEL_KEYS, status) ? STATUS_LABEL_KEYS[status] : UNKNOWN_STATUS_LABEL_KEY;

interface PillStyle {
    status?: PillsStatus;
    variant: PillsVariant;
    className?: string;
}

/** The same pill per tone as the cycle rows render, so a status reads identically everywhere;
 * `completed` has no `Pills` status of its own and is drawn as a bordered neutral pill. */
const TONE_PILL_STYLES: Readonly<Record<CycleStatusTone, PillStyle>> = {
    active: { status: 'active', variant: 'activeLight' },
    draft: { status: 'draft', variant: 'draftLight' },
    inactive: { status: 'inactive', variant: 'inactiveLight' },
    completed: { variant: 'default', className: 'border border-base-300' },
};

export const getObjectivePeriodPillStyle = (status: ObjectivePeriodStatusType): PillStyle =>
    TONE_PILL_STYLES[getObjectivePeriodStatusTone(status)];

/** The calendar is coloured only while the period is live, the same tone rule the cycle rows
 * follow; the colour itself is the turquoise the prototype gives the calendar, which is what
 * distinguishes an objective period from an evaluation cycle at a glance. */
const TONE_ICON_COLORS: Readonly<Record<CycleStatusTone, ColorType>> = {
    active: Colors.PRIMARY,
    draft: Colors.BASE_400,
    inactive: Colors.BASE_400,
    completed: Colors.BASE_400,
};

export const getObjectivePeriodRowIconColor = (status: ObjectivePeriodStatusType): ColorType =>
    TONE_ICON_COLORS[getObjectivePeriodStatusTone(status)];
